import { twMerge } from "@lumen-energy/design-system"
import { Popover } from "@lumen-energy/design-system"

import { AddLabelPopoverContentProps } from "./AddLabelPopoverContent"
import { EditOrgLabelPopoverContent } from "./EditOrgLabelPopoverContent"
import { EditSiteLabelPopoverContent } from "./EditSiteLabelPopoverContent"
import { LabelProps } from "./Label"
import { Label } from "./Label"
import { LabelGroupProps, TempLabelsProps } from "./LabelGroup"

interface ExistingLabelsListProps
  extends Pick<
      LabelGroupProps,
      "editLabelColor" | "editLabelName" | "orgLabels" | "deleteLabel"
    >,
    Pick<AddLabelPopoverContentProps, "isBulkAction"> {
  className?: string
  // "all" shows all labels that exist on for an Org; "site" shows labels applied to current site only
  labelsToShow?: "all" | "site"
  handleSendBatchedUpdate?: () => void
  tempLabels: Array<LabelProps>
  removeLabel?: (labelPid: string, labelName: string) => void
}

export function ExistingLabelsList({
  className,
  editLabelColor,
  editLabelName,
  labelsToShow = "all",
  orgLabels = [],
  removeLabel = () => {},
  tempLabels,
  setTempLabels,
  deleteLabel = () => {},
  isBulkAction,
}: ExistingLabelsListProps & TempLabelsProps) {
  const mapObj = labelsToShow === "all" ? orgLabels : tempLabels

  const handleBatchedAddLabels = (labelToAdd: LabelProps) => {
    const isLabelAlreadyBatched = tempLabels?.find(
      (label) => label.name === labelToAdd.name,
    )

    // Ignore labels that are already batched
    if (isLabelAlreadyBatched) return

    setTempLabels((prev) => [
      ...prev,
      { pid: labelToAdd.pid, name: labelToAdd.name, themeColor: labelToAdd.themeColor },
    ])
  }

  return (
    <div
      className={twMerge("flex max-h-[225px] flex-col overflow-y-auto pb-2", className)}
    >
      {mapObj.length > 0 &&
        mapObj.map((label) => {
          const isAlreadyApplied = tempLabels.find(
            (appliedLabel: LabelProps) => appliedLabel.name === label.name,
          )

          return (
            <div
              key={label.pid}
              className={twMerge(
                "flex flex-row justify-between py-2 pl-1",
                labelsToShow === "all" && !isAlreadyApplied ? "hover:bg-grey-200" : "",
              )}
            >
              <Label
                name={label.name}
                themeColor={label.themeColor}
                // "site" labels should not appear clickable (cursor-pointer)
                // "org" labels have a background color on hover
                className={
                  labelsToShow === "site" || isAlreadyApplied ? "cursor-default" : ""
                }
                pid={label.pid}
                onClick={
                  !isAlreadyApplied ? () => handleBatchedAddLabels(label) : () => {}
                }
              />

              <Popover.Root>
                <Popover
                  align="start"
                  side="right"
                  sideOffset={30}
                  content={
                    // For bulk actions, we want to show the Site Label Editing options (to allow for bulk removal)
                    labelsToShow === "site" || isBulkAction ? (
                      // Editing Site labels allows for site-level label removal
                      <EditSiteLabelPopoverContent
                        editLabelColor={editLabelColor}
                        editLabelName={editLabelName}
                        label={label}
                        orgLabels={orgLabels}
                        removeLabel={removeLabel}
                      />
                    ) : (
                      // Editing Org labels allows for org-level label deletion
                      <EditOrgLabelPopoverContent
                        editLabelColor={editLabelColor}
                        editLabelName={editLabelName}
                        label={label}
                        orgLabels={orgLabels}
                        deleteLabel={deleteLabel}
                      />
                    )
                  }
                  className={twMerge("max-w-[250px]", className)}
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  <div className="mx-2 h-[26px] w-[26px] cursor-pointer rounded-full text-center hover:bg-haze-300">
                    &#x2026;
                  </div>
                </Popover>
              </Popover.Root>
            </div>
          )
        })}
    </div>
  )
}
